import { Suspense, lazy, useCallback, useEffect, useState } from "react";
import {
  AlertCircle,
  ArrowLeft,
  Banknote,
  CreditCard,
  Landmark,
  ArrowRight,
  Calendar,
  Car,
  Cat,
  Check,
  Clock,
  Dog,
  Loader2,
  MapPin,
  MessageCircle,
  PawPrint,
  Radar,
  Route as RouteIcon,
  ShieldCheck,
  User,
} from "lucide-react";
import { ClientOnly, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { getDrivingRoute, type Coord } from "@/lib/geo";
import { AddressFields, emptyAddress, formatAddress, type AddressParts } from "./AddressFields";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { paymentsConfigured } from "@/lib/stripe";

const TripCheckout = lazy(() =>
  import("./TripCheckout").then((m) => ({ default: m.TripCheckout })),
);

import {
  buildWhatsAppUrlWithQuote,
  METODO_PAGO_LABEL,
  WHATSAPP_NUMBER,
  type BookingData,
  type MetodoPago,
  SPEI,
  speiConfigurado,
  conceptoPago,
} from "@/lib/petmoon";
import { getQuote } from "@/lib/quote.functions";
import {
  BASE_FARE_MXN,
  PER_KM_MXN,
  BUFFER_MINUTES,
  SLOT_MINUTES,
  formatMXN,
  formatSlotLabel,
  generateTimeSlots,
  isSlotOccupied,
  type BusyRange,
  type QuoteResult,
} from "@/lib/pricing";
import { priceFor } from "@/lib/pricing";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { SafetyPolicyModal } from "./SafetyPolicyModal";

const RouteMap = lazy(() => import("./RouteMap"));

function MapShell({
  origen,
  destino,
  geometry,
}: {
  origen: Coord | null;
  destino: Coord | null;
  geometry: Array<[number, number]>;
}) {
  return (
    <div className="mt-5 h-64 overflow-hidden rounded-2xl border-[1.5px] border-input sm:h-80">
      <ClientOnly fallback={<div className="h-full w-full animate-pulse bg-secondary" />}>
        <Suspense fallback={<div className="h-full w-full animate-pulse bg-secondary" />}>
          <RouteMap origen={origen} destino={destino} geometry={geometry} />
        </Suspense>
      </ClientOnly>
    </div>
  );
}



const petTypes = [
  { value: "Perro", icon: Dog },
  { value: "Gato", icon: Cat },
  { value: "Otro", icon: PawPrint },
] as const;

const sizes = [
  { value: "Pequeño", hint: "hasta 10 kg" },
  { value: "Mediano", hint: "10–25 kg" },
  { value: "Grande", hint: "+25 kg" },
] as const;

const stepMeta = [
  { label: "Ruta", icon: MapPin },
  { label: "Mascota", icon: PawPrint },
  { label: "Fecha y hora", icon: Calendar },
  { label: "Confirmar", icon: Check },
];

const stepTitles = [
  "¿De dónde a dónde vamos?",
  "¿Quién viaja hoy?",
  "¿Cuándo lo necesitas?",
  "Revisa y confirma tu viaje",
];

const initialData: BookingData = {
  origen: "",
  origenReferencias: "",
  destino: "",
  destinoReferencias: "",
  idaYVuelta: false,
  tipoMascota: "",
  nombreMascota: "",
  tamano: "",
  notas: "",
  fecha: "",
  hora: "",
  duenoViaja: "",
  contactoDestino: "",
  clienteNombre: "",
  clienteTelefono: "",
  metodoPago: "efectivo",
};

const SLOTS = generateTimeSlots();

export function BookingWizard() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState<BookingData>(initialData);
  const [busy, setBusy] = useState<BusyRange[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [quote, setQuote] = useState<QuoteResult | null>(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [booking, setBooking] = useState(false);
  const [bookingId, setBookingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [acepta, setAcepta] = useState(false);
  const [searching, setSearching] = useState(false);
  const [rechazado, setRechazado] = useState(false);
  const [origenParts, setOrigenParts] = useState<AddressParts>(emptyAddress);
  const [destinoParts, setDestinoParts] = useState<AddressParts>(emptyAddress);
  const [origenCoord, setOrigenCoord] = useState<Coord | null>(null);
  const [destinoCoord, setDestinoCoord] = useState<Coord | null>(null);
  const [geometry, setGeometry] = useState<Array<[number, number]>>([]);
  const navigate = useNavigate();

  const quoteFn = useServerFn(getQuote);

  const update = <K extends keyof BookingData>(key: K, value: BookingData[K]) =>
    setData((prev) => ({ ...prev, [key]: value }));

  const loadSlots = useCallback(async (fecha: string) => {
    if (!fecha) return;
    setLoadingSlots(true);
    const { data: rows, error: rpcError } = await supabase.rpc("slots_ocupados", {
      p_fecha: fecha,
    });
    setBusy(rpcError || !rows ? [] : (rows as BusyRange[]));
    setLoadingSlots(false);
  }, []);

  useEffect(() => {
    void loadSlots(data.fecha);
  }, [data.fecha, loadSlots]);

  // Ruta real de manejo (OSRM) en cuanto hay origen y destino con coordenadas.
  useEffect(() => {
    if (!origenCoord || !destinoCoord) {
      setGeometry([]);
      return;
    }
    let active = true;
    const controller = new AbortController();
    setLoadingQuote(true);
    getDrivingRoute(origenCoord, destinoCoord, controller.signal)
      .then((route) => {
        if (!active) return;
        setGeometry(route.geometry);
        const factor = data.idaYVuelta ? 2 : 1;
        setQuote({
          distanciaKm: Math.round(route.distanciaKm * factor * 10) / 10,
          duracionMin: route.duracionMin * factor,
          precioMxn: priceFor(route.distanciaKm, data.idaYVuelta),
          aproximado: false,
        });
      })
      .catch(() => {
        if (active) setQuote(null);
      })
      .finally(() => {
        if (active) setLoadingQuote(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [origenCoord, destinoCoord, data.idaYVuelta]);

  // Respaldo: si no se eligió sugerencia con coordenadas, cotiza en el servidor.
  useEffect(() => {
    if (step !== 3) return;
    if (origenCoord && destinoCoord) return;
    let active = true;
    setLoadingQuote(true);
    quoteFn({
      data: {
        origen: data.origen.trim(),
        destino: data.destino.trim(),
        idaYVuelta: data.idaYVuelta,
      },
    })
      .then((res) => {
        if (active) setQuote(res);
      })
      .catch(() => {
        if (active) setQuote(null);
      })
      .finally(() => {
        if (active) setLoadingQuote(false);
      });
    return () => {
      active = false;
    };
  }, [
    step,
    data.origen,
    data.destino,
    data.idaYVuelta,
    quoteFn,
    origenCoord,
    destinoCoord,
  ]);

  const isStepValid = (s: number): boolean => {
    switch (s) {
      case 0:
        return (
          origenParts.calle.trim().length > 2 &&
          origenParts.zona.trim().length > 2 &&
          destinoParts.calle.trim().length > 2 &&
          destinoParts.zona.trim().length > 2
        );
      case 1:
        return (
          data.tipoMascota !== "" &&
          data.nombreMascota.trim().length > 0 &&
          data.tamano !== ""
        );
      case 2:
        return (
          data.fecha !== "" &&
          data.hora !== "" &&
          !isSlotOccupied(data.hora, busy) &&
          data.duenoViaja !== ""
        );
      default:
        return true;
    }
  };

  const confirmBooking = async () => {
    setBooking(true);
    setError(null);
    const { data: rows, error: rpcError } = await supabase.rpc("crear_reserva", {
      p_fecha: data.fecha,
      p_hora: data.hora,
      p_duracion_slot_min: SLOT_MINUTES,
      p_buffer_min: BUFFER_MINUTES,
      p_origen: data.origen.trim(),
      p_destino: data.destino.trim(),
      p_ida_y_vuelta: data.idaYVuelta,
      p_tipo_mascota: data.tipoMascota,
      p_nombre_mascota: data.nombreMascota.trim(),
      p_tamano: data.tamano,
      p_notas: [
        data.notas.trim(),
        data.origenReferencias.trim() ? `Ref. origen: ${data.origenReferencias.trim()}` : "",
        data.destinoReferencias.trim() ? `Ref. destino: ${data.destinoReferencias.trim()}` : "",
        data.duenoViaja === "no" && data.contactoDestino.trim()
          ? `Contacto en destino: ${data.contactoDestino.trim()}`
          : "",
      ]
        .filter(Boolean)
        .join(" | "),
      p_dueno_viaja: data.duenoViaja === "si",
      p_distancia_km: quote?.distanciaKm ?? 0,
      p_duracion_min: quote?.duracionMin ?? 0,
      p_precio_mxn: quote?.precioMxn ?? 0,
      p_metodo_pago: data.metodoPago,
      p_cliente_nombre: data.clienteNombre.trim(),
      p_cliente_telefono: data.clienteTelefono.trim(),
    });
    setBooking(false);

    const result = Array.isArray(rows) ? rows[0] : null;
    if (rpcError || !result || !result.ok) {
      setError(
        result?.mensaje ??
          "No pudimos guardar tu reserva. Intenta de nuevo en unos segundos.",
      );
      await loadSlots(data.fecha);
      return;
    }
    setBookingId(result.id as string);
    window.open(buildWhatsAppUrlWithQuote(data, quote), "_blank", "noopener,noreferrer");

    // Estado PENDING_ACCEPTANCE: esperamos a que el equipo acepte el viaje.
    setRechazado(false);
    setSearching(true);
  };

  // Mientras esperamos, consultamos el estado de la solicitud cada 4 s.
  useEffect(() => {
    if (!searching || !bookingId) return;
    let alive = true;
    const check = async () => {
      const { data: rows } = await supabase.rpc("estado_reserva", { p_id: bookingId });
      const row = Array.isArray(rows) ? rows[0] : null;
      if (!alive || !row) return;
      if (row.estado_solicitud === "aceptado") {
        alive = false;
        const nombre = data.nombreMascota.trim();
        void navigate({
          to: "/seguimiento",
          search: nombre ? { mascota: nombre } : {},
        });
      } else if (row.estado_solicitud === "rechazado") {
        alive = false;
        setSearching(false);
        setRechazado(true);
      }
    };
    const id = window.setInterval(() => void check(), 4000);
    void check();
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [searching, bookingId, data.nombreMascota, navigate]);




  const canContinue = isStepValid(step);
  const today = new Date().toISOString().split("T")[0];

  const summaryRows: Array<[string, string]> = [
    ["Origen", data.origen.trim()],
    ...(data.origenReferencias.trim()
      ? ([["Referencias origen", data.origenReferencias.trim()]] as Array<[string, string]>)
      : []),
    ["Destino", data.destino.trim()],
    ...(data.destinoReferencias.trim()
      ? ([["Referencias destino", data.destinoReferencias.trim()]] as Array<[string, string]>)
      : []),
    ["Tipo de viaje", data.idaYVuelta ? "Ida y vuelta" : "Solo ida"],
    ["Mascota", `${data.nombreMascota.trim()} · ${data.tipoMascota} · ${data.tamano}`],
    ...(data.notas.trim() ? ([["Notas", data.notas.trim()]] as Array<[string, string]>) : []),
    ["Fecha y hora", `${data.fecha} · ${data.hora ? formatSlotLabel(data.hora) : ""}`],
    [
      "¿Quién viaja?",
      data.duenoViaja === "si" ? "Con dueño/acompañante" : "Solo la mascota",
    ],
    ...(data.duenoViaja === "no" && data.contactoDestino.trim()
      ? ([["Contacto en destino", data.contactoDestino.trim()]] as Array<[string, string]>)
      : []),
  ];

  if (searching) {
    return (
      <section id="reservar" className="bg-secondary/40 py-20 sm:py-24">
        <div className="mx-auto max-w-3xl px-5">
          <div className="animate-step-in rounded-[2rem] bg-night px-6 py-16 text-center text-night-foreground shadow-card">
            <span className="relative mx-auto flex h-24 w-24 items-center justify-center">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent/30" />
              <span className="relative flex h-20 w-20 items-center justify-center rounded-full bg-accent/15 ring-1 ring-accent/40">
                <Radar className="h-9 w-9 animate-spin text-accent [animation-duration:3s]" aria-hidden />
              </span>
            </span>
            <h3 className="mt-8 font-display text-2xl font-bold">
              Solicitud Enviada 🐾
            </h3>
            <p className="mx-auto mt-3 max-w-sm text-sm font-semibold leading-relaxed text-night-foreground/70">
              ¡Gracias! Tu solicitud ha sido enviada por WhatsApp. En un momento te
              confirmaremos la disponibilidad de la unidad.
            </p>


            <div className="mx-auto mt-7 max-w-sm rounded-2xl bg-night-foreground/10 px-5 py-4 text-left">
              <p className="text-[11px] font-extrabold uppercase tracking-widest text-accent">
                Método de pago elegido
              </p>
              <p className="mt-1 text-sm font-bold">{METODO_PAGO_LABEL[data.metodoPago]}</p>
              {data.metodoPago === "transferencia" && (
                speiConfigurado() ? (
                  <div className="mt-3 space-y-1.5 rounded-xl bg-night-foreground/10 px-4 py-3 text-xs font-semibold text-night-foreground/85">
                    <p className="text-[11px] font-extrabold uppercase tracking-widest text-accent">
                      Datos para tu transferencia
                    </p>
                    <p>Banco: <span className="font-bold">{SPEI.banco}</span></p>
                    <p>Beneficiario: <span className="font-bold">{SPEI.beneficiario}</span></p>
                    <p>CLABE: <span className="font-bold tracking-wider">{SPEI.clabe}</span></p>
                    <p>Concepto: <span className="font-bold">{conceptoPago(data.clienteNombre)}</span></p>
                    <button
                      type="button"
                      onClick={() => void navigator.clipboard?.writeText(SPEI.clabe)}
                      className="mt-1 rounded-full bg-accent px-4 py-2 text-[11px] font-extrabold uppercase tracking-widest text-night"
                    >
                      Copiar CLABE
                    </button>
                    <p className="pt-1 text-night-foreground/60">
                      Envíanos tu comprobante por WhatsApp para verificar el pago.
                    </p>
                  </div>
                ) : (
                  <p className="mt-2 text-xs font-semibold leading-relaxed text-night-foreground/70">
                    Te enviamos por WhatsApp los datos de transferencia SPEI / el enlace de
                    Mercado Pago para completar tu pago. Tu lugar ya quedó apartado.
                  </p>
                )
              )}
              {data.metodoPago === "tarjeta" && (
                <p className="mt-2 text-xs font-semibold leading-relaxed text-night-foreground/70">
                  Paga ahora con tarjeta de forma segura; tu lugar ya está apartado.
                </p>
              )}

              {data.metodoPago === "efectivo" && (
                <p className="mt-2 text-xs font-semibold leading-relaxed text-night-foreground/70">
                  Pagas al abordar, en efectivo o con terminal en la unidad.
                </p>
              )}
            </div>

            {data.metodoPago === "tarjeta" && quote && paymentsConfigured() && (
              <div className="mx-auto mt-6 max-w-md text-left">
                <PaymentTestModeBanner />
                <Suspense
                  fallback={
                    <p className="py-6 text-center text-xs font-bold text-night-foreground/60">
                      Cargando pago seguro…
                    </p>
                  }
                >
                  <TripCheckout
                    montoMxn={quote.precioMxn}
                    referencia={`${data.nombreMascota.trim() || "Mascota"} · ${data.fecha} ${data.hora}`}
                    returnUrl={`${typeof window !== "undefined" ? window.location.origin : ""}/seguimiento`}
                  />
                </Suspense>
              </div>
            )}



            <button
              type="button"
              onClick={() => setSearching(false)}
              className="mt-7 text-xs font-extrabold uppercase tracking-widest text-night-foreground/60 hover:text-accent"
            >
              Cancelar espera
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (rechazado) {
    return (
      <section id="reservar" className="bg-secondary/40 py-20 sm:py-24">
        <div className="mx-auto max-w-xl px-5">
          <div
            role="alertdialog"
            aria-labelledby="rechazo-title"
            className="animate-step-in rounded-[2rem] bg-card px-6 py-12 text-center shadow-card ring-1 ring-border"
          >
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
              <AlertCircle className="h-8 w-8 text-destructive" aria-hidden />
            </span>
            <h3 id="rechazo-title" className="mt-6 font-display text-2xl font-bold text-foreground">
              No hay unidades disponibles
            </h3>
            <p className="mx-auto mt-3 max-w-sm text-sm font-semibold leading-relaxed text-muted-foreground">
              No hay unidades disponibles en este momento. Intenta de nuevo o contáctanos por
              WhatsApp y buscamos un horario para {data.nombreMascota.trim() || "tu peludo"}.
            </p>
            <div className="mt-7 grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => {
                  setRechazado(false);
                  setBookingId(null);
                  setError(null);
                  setStep(2);
                  void loadSlots(data.fecha);
                }}
                className="rounded-full bg-primary px-6 py-3.5 text-sm font-extrabold text-primary-foreground shadow-soft transition-all hover:-translate-y-0.5"
              >
                Intentar de nuevo
              </button>
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
                  `Hola PetMoon 🌙 mi solicitud no pudo confirmarse, ¿tienen otro horario para ${data.nombreMascota.trim() || "mi mascota"}?`,
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 rounded-full bg-whatsapp px-6 py-3.5 text-sm font-extrabold text-whatsapp-foreground shadow-soft transition-all hover:-translate-y-0.5"
              >
                <MessageCircle className="h-4 w-4" aria-hidden />
                Contactar por WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>
    );
  }



  return (
    <section id="reservar" className="bg-secondary/40 py-20 sm:py-24">
      <div className="mx-auto max-w-3xl px-5">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-display text-3xl font-bold tracking-tight text-primary sm:text-4xl">
            Reserva tu viaje
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Cuéntanos a dónde va tu peludo y nosotros nos encargamos del resto.
            Confirmamos tu solicitud por WhatsApp en minutos.
          </p>
        </div>

        <div className="mt-10 rounded-[2rem] bg-card p-6 shadow-card ring-1 ring-border sm:p-9">
          {/* Progress */}
          <ol className="flex items-center" aria-label="Progreso de la reserva">
            {stepMeta.map((meta, i) => {
              const done = i < step;
              const current = i === step;
              return (
                <li key={meta.label} className={cn("flex items-center", i > 0 && "flex-1")}>
                  {i > 0 && (
                    <span
                      aria-hidden
                      className={cn(
                        "mx-2 h-0.5 flex-1 rounded-full transition-colors sm:mx-3",
                        done || current ? "bg-accent" : "bg-border",
                      )}
                    />
                  )}
                  <div
                    className="flex flex-col items-center gap-1.5"
                    aria-current={current ? "step" : undefined}
                  >
                    <span
                      className={cn(
                        "flex h-10 w-10 items-center justify-center rounded-full transition-colors",
                        done && "bg-accent text-accent-foreground",
                        current && "bg-primary text-primary-foreground ring-4 ring-accent/40",
                        !done && !current && "bg-secondary text-muted-foreground",
                      )}
                    >
                      {done ? (
                        <Check className="h-5 w-5" aria-hidden />
                      ) : (
                        <meta.icon className="h-5 w-5" aria-hidden />
                      )}
                    </span>
                    <span
                      className={cn(
                        "text-[11px] font-bold sm:text-xs",
                        current ? "text-primary" : "text-muted-foreground",
                      )}
                    >
                      {meta.label}
                    </span>
                  </div>
                </li>
              );
            })}
          </ol>

          {/* Step content */}
          <div key={step} className="mt-8 animate-step-in">
            <h3 className="font-display text-2xl font-bold text-foreground">
              {stepTitles[step]}
            </h3>

            {step === 0 && (
              <div className="mt-6 space-y-5">
                <fieldset className="rounded-2xl border-[1.5px] border-border bg-secondary/40 p-4">
                  <legend className="px-2 font-display text-sm font-extrabold text-foreground">
                    Ruta del viaje
                  </legend>
                  <div className="space-y-6">
                    <AddressFields
                      titulo="Origen (recolección)"
                      value={origenParts}
                      onChange={(v) => {
                        setOrigenParts(v);
                        update("origen", formatAddress(v));
                        update("origenReferencias", v.referencias);
                      }}
                      onZoneCoord={setOrigenCoord}
                      placeholderCalle="Calle y número donde recogemos"
                      placeholderZona="Colonia y municipio de recolección"
                    />
                    <div className="border-t border-border" aria-hidden />
                    <AddressFields
                      titulo="Destino (entrega)"
                      value={destinoParts}
                      onChange={(v) => {
                        setDestinoParts(v);
                        update("destino", formatAddress(v));
                        update("destinoReferencias", v.referencias);
                      }}
                      onZoneCoord={setDestinoCoord}
                      placeholderCalle="Calle y número de entrega"
                      placeholderZona="Colonia y municipio de entrega"
                    />
                  </div>
                </fieldset>
                <fieldset>
                  <legend className="field-label">Tipo de viaje</legend>
                  <div className="grid grid-cols-2 gap-2 rounded-2xl bg-secondary/70 p-1.5 ring-1 ring-border">
                    {(
                      [
                        { value: false, label: "Solo Ida" },
                        { value: true, label: "Ida y Vuelta" },
                      ] as const
                    ).map((opt) => (
                      <button
                        key={String(opt.value)}
                        type="button"
                        aria-pressed={data.idaYVuelta === opt.value}
                        onClick={() => update("idaYVuelta", opt.value)}
                        className={cn(
                          "rounded-xl px-4 py-3 text-sm font-extrabold transition-all",
                          data.idaYVuelta === opt.value
                            ? "bg-accent text-accent-foreground shadow-soft"
                            : "text-muted-foreground hover:text-foreground",
                        )}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <p className="mt-2 text-xs font-semibold text-muted-foreground">
                    En ida y vuelta se cobra la distancia completa del recorrido redondo.
                  </p>
                </fieldset>


                <MapShell origen={origenCoord} destino={destinoCoord} geometry={geometry} />

                {origenCoord && destinoCoord && (
                  <div className="rounded-2xl bg-night p-4 text-night-foreground ring-1 ring-accent/30">
                    {loadingQuote || !quote ? (
                      <p className="flex items-center justify-center gap-2 text-sm font-semibold">
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                        Calculando ruta y tarifa…
                      </p>
                    ) : (
                      <>
                        <p className="text-center text-sm font-bold">
                          Distancia estimada: {quote.distanciaKm} km | Tiempo:{" "}
                          {quote.duracionMin} mins
                        </p>
                        <p className="mt-1 text-center text-2xl font-extrabold text-accent">
                          {formatMXN(quote.precioMxn)}
                        </p>
                        <p className="mt-1 text-center text-[11px] font-semibold opacity-70">
                          Banderazo: {formatMXN(BASE_FARE_MXN)} | Distancia:{" "}
                          {quote.distanciaKm} km (${PER_KM_MXN}/km)
                        </p>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {step === 1 && (
              <div className="mt-6 space-y-6">
                <fieldset>
                  <legend className="field-label">
                    Tipo de mascota <span className="text-destructive">*</span>
                  </legend>
                  <div className="grid grid-cols-3 gap-3">
                    {petTypes.map((pet) => (
                      <button
                        key={pet.value}
                        type="button"
                        aria-pressed={data.tipoMascota === pet.value}
                        onClick={() => update("tipoMascota", pet.value)}
                        className={cn(
                          "flex flex-col items-center gap-2 rounded-2xl border-2 px-3 py-4 transition-all",
                          data.tipoMascota === pet.value
                            ? "border-accent bg-gold-soft shadow-soft"
                            : "border-input bg-background hover:border-accent/50",
                        )}
                      >
                        <pet.icon
                          className={cn(
                            "h-7 w-7",
                            data.tipoMascota === pet.value
                              ? "text-gold-deep"
                              : "text-muted-foreground",
                          )}
                          aria-hidden
                        />
                        <span className="text-sm font-extrabold text-foreground">
                          {pet.value}
                        </span>
                      </button>
                    ))}
                  </div>
                </fieldset>

                <div>
                  <label htmlFor="nombreMascota" className="field-label">
                    Nombre de tu mascota <span className="text-destructive">*</span>
                  </label>
                  <input
                    id="nombreMascota"
                    type="text"
                    className="field-input"
                    placeholder="Ej. Rocky"
                    value={data.nombreMascota}
                    onChange={(e) => update("nombreMascota", e.target.value)}
                    maxLength={60}
                  />
                </div>

                <fieldset>
                  <legend className="field-label">
                    Tamaño <span className="text-destructive">*</span>
                  </legend>
                  <div className="grid grid-cols-3 gap-3">
                    {sizes.map((size) => (
                      <button
                        key={size.value}
                        type="button"
                        aria-pressed={data.tamano === size.value}
                        onClick={() => update("tamano", size.value)}
                        className={cn(
                          "rounded-2xl border-2 px-3 py-3.5 text-center transition-all",
                          data.tamano === size.value
                            ? "border-accent bg-gold-soft shadow-soft"
                            : "border-input bg-background hover:border-accent/50",
                        )}
                      >
                        <span className="block text-sm font-extrabold text-foreground">
                          {size.value}
                        </span>
                        <span className="mt-0.5 block text-xs font-semibold text-muted-foreground">
                          {size.hint}
                        </span>
                      </button>
                    ))}
                  </div>
                </fieldset>

                <div>
                  <label htmlFor="notas" className="field-label">
                    Comentarios importantes{" "}
                    <span className="font-semibold text-muted-foreground">(opcional)</span>
                  </label>
                  <textarea
                    id="notas"
                    className="field-input min-h-24 resize-y"
                    placeholder="Ej. Se marea en el coche, es nervioso con extraños, lleva su transportadora…"
                    value={data.notas}
                    onChange={(e) => update("notas", e.target.value)}
                    maxLength={500}
                  />
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="mt-6 space-y-6">
                <div>
                  <label htmlFor="fecha" className="field-label">
                    <Calendar className="mr-1.5 inline h-4 w-4 text-gold-deep" aria-hidden />
                    Fecha del viaje <span className="text-destructive">*</span>
                  </label>
                  <input
                    id="fecha"
                    type="date"
                    className="field-input"
                    min={today}
                    value={data.fecha}
                    onChange={(e) => {
                      update("fecha", e.target.value);
                      update("hora", "");
                    }}
                  />
                </div>

                <fieldset>
                  <legend className="field-label">
                    <Clock className="mr-1.5 inline h-4 w-4 text-gold-deep" aria-hidden />
                    Franja horaria de recogida <span className="text-destructive">*</span>
                  </legend>
                  {!data.fecha ? (
                    <p className="rounded-2xl bg-muted/60 px-4 py-3 text-sm font-semibold text-muted-foreground">
                      Elige primero la fecha para ver los horarios disponibles.
                    </p>
                  ) : loadingSlots ? (
                    <p className="flex items-center gap-2 px-1 text-sm font-semibold text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      Consultando disponibilidad…
                    </p>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
                        {SLOTS.map((slot) => {
                          const occupied = isSlotOccupied(slot, busy);
                          const selected = data.hora === slot;
                          return (
                            <button
                              key={slot}
                              type="button"
                              disabled={occupied}
                              aria-pressed={selected}
                              onClick={() => update("hora", slot)}
                              className={cn(
                                "rounded-2xl border-2 px-2 py-3 text-sm font-extrabold transition-all",
                                occupied
                                  ? "cursor-not-allowed border-border bg-muted text-muted-foreground/60 line-through"
                                  : selected
                                    ? "border-accent bg-gold-soft text-foreground shadow-soft"
                                    : "border-input bg-background text-primary hover:border-accent/60",
                              )}
                            >
                              {formatSlotLabel(slot)}
                              {occupied && (
                                <span className="mt-1 block text-[10px] font-bold uppercase tracking-wide no-underline">
                                  Ocupado
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                      <p className="mt-3 text-xs font-semibold text-muted-foreground">
                        Cada viaje reserva {SLOT_MINUTES} min + {BUFFER_MINUTES} min de margen
                        para sanitización pet-safe y tráfico.
                      </p>
                    </>
                  )}
                </fieldset>


                <fieldset>
                  <legend className="field-label">
                    <User className="mr-1.5 inline h-4 w-4 text-gold-deep" aria-hidden />
                    ¿Quién viaja? <span className="text-destructive">*</span>
                  </legend>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {(
                      [
                        { value: "si", label: "Viaja Mascota con Dueño/Acompañante" },
                        { value: "no", label: "Viaja Solo la Mascota" },
                      ] as const
                    ).map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        aria-pressed={data.duenoViaja === option.value}
                        onClick={() => update("duenoViaja", option.value)}
                        className={cn(
                          "rounded-2xl border-2 px-4 py-3.5 text-sm font-extrabold transition-all",
                          data.duenoViaja === option.value
                            ? "border-accent bg-gold-soft text-foreground shadow-soft"
                            : "border-input bg-background text-muted-foreground hover:border-accent/50",
                        )}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  {data.duenoViaja === "no" && (
                    <div className="mt-3">
                      <label htmlFor="contactoDestino" className="field-label">
                        Nombre y teléfono de quien recibe en destino (opcional)
                      </label>
                      <input
                        id="contactoDestino"
                        type="text"
                        value={data.contactoDestino}
                        onChange={(e) => update("contactoDestino", e.target.value)}
                        placeholder="Ej. Ana López · 33 1234 5678"
                        className="w-full rounded-2xl border-[1.5px] border-input bg-background px-4 py-3.5 text-sm font-semibold text-foreground outline-none transition-colors focus:border-accent"
                      />
                    </div>
                  )}
                </fieldset>

              </div>
            )}

            {step === 3 && (
              <div className="mt-6">
                <dl className="divide-y divide-border rounded-2xl bg-muted/60 px-5 ring-1 ring-border">
                  {summaryRows.map(([label, value]) => (
                    <div
                      key={label}
                      className="flex flex-col gap-0.5 py-3.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-6"
                    >
                      <dt className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                        {label}
                      </dt>
                      <dd className="text-sm font-bold text-foreground sm:text-right">
                        {value}
                      </dd>
                    </div>
                  ))}
                </dl>

                {/* Datos de contacto */}
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <label className="block">
                    <span className="field-label">
                      Tu nombre <span className="text-destructive">*</span>
                    </span>
                    <input
                      type="text"
                      maxLength={80}
                      value={data.clienteNombre}
                      onChange={(e) => update("clienteNombre", e.target.value)}
                      placeholder="Ej. Ana García"
                      className="w-full rounded-2xl border-[1.5px] border-input bg-background px-4 py-3.5 text-sm font-semibold outline-none focus:border-accent"
                    />
                  </label>
                  <label className="block">
                    <span className="field-label">
                      WhatsApp de contacto <span className="text-destructive">*</span>
                    </span>
                    <input
                      type="tel"
                      maxLength={20}
                      value={data.clienteTelefono}
                      onChange={(e) => update("clienteTelefono", e.target.value)}
                      placeholder="Ej. 33 1234 5678"
                      className="w-full rounded-2xl border-[1.5px] border-input bg-background px-4 py-3.5 text-sm font-semibold outline-none focus:border-accent"
                    />
                  </label>
                </div>

                {/* Método de pago */}
                <fieldset className="mt-5">
                  <legend className="field-label">
                    <CreditCard className="mr-1.5 inline h-4 w-4 text-gold-deep" aria-hidden />
                    Método de pago <span className="text-destructive">*</span>
                  </legend>
                  <div className="grid gap-3">
                    {(
                      [
                        { value: "efectivo", icon: Banknote, hint: "Pagas al abordar la unidad" },
                        { value: "transferencia", icon: Landmark, hint: "Te enviamos los datos por WhatsApp" },
                        { value: "tarjeta", icon: CreditCard, hint: "Enlace de pago seguro en línea" },
                      ] as const
                    ).map((m) => (
                      <button
                        key={m.value}
                        type="button"
                        aria-pressed={data.metodoPago === m.value}
                        onClick={() => update("metodoPago", m.value as MetodoPago)}
                        className={cn(
                          "flex items-center gap-3 rounded-2xl border-2 px-4 py-3.5 text-left transition-all",
                          data.metodoPago === m.value
                            ? "border-accent bg-gold-soft shadow-soft"
                            : "border-input bg-background hover:border-accent/50",
                        )}
                      >
                        <m.icon className="h-5 w-5 shrink-0 text-gold-deep" aria-hidden />
                        <span className="min-w-0">
                          <span className="block text-sm font-extrabold text-foreground">
                            {METODO_PAGO_LABEL[m.value]}
                          </span>
                          <span className="block text-xs font-semibold text-muted-foreground">
                            {m.hint}
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                </fieldset>



                {/* Cotización dinámica */}
                <div className="mt-5 rounded-2xl bg-night p-5 text-night-foreground ring-1 ring-accent/30">
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-accent">
                    <RouteIcon className="h-4 w-4" aria-hidden />
                    Cotización estimada
                  </div>
                  {loadingQuote ? (
                    <p className="mt-3 flex items-center gap-2 text-sm font-semibold">
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      Calculando distancia y tarifa…
                    </p>
                  ) : quote ? (
                    <>
                      <div className="mt-3 grid grid-cols-3 gap-3 text-center">
                        <div>
                          <p className="text-lg font-extrabold">{quote.distanciaKm} km</p>
                          <p className="text-[11px] font-bold opacity-70">Distancia</p>
                        </div>
                        <div>
                          <p className="text-lg font-extrabold">{quote.duracionMin} min</p>
                          <p className="text-[11px] font-bold opacity-70">Duración</p>
                        </div>
                        <div>
                          <p className="text-lg font-extrabold text-accent">
                            {formatMXN(quote.precioMxn)}
                          </p>
                          <p className="text-[11px] font-bold opacity-70">Total</p>
                        </div>
                      </div>
                      <p className="mt-3 text-center text-[11px] font-semibold opacity-70">
                        Banderazo: {formatMXN(BASE_FARE_MXN)} | Distancia: {quote.distanciaKm} km ($
                        {PER_KM_MXN}/km)
                        {quote.aproximado ? " · estimación aproximada" : ""}. Se confirma por
                        WhatsApp.
                      </p>
                      <p className="mt-2.5 flex items-center justify-center gap-1.5 text-center text-[11px] font-bold text-accent">
                        ✨ Incluye arnés de seguridad adaptado, unidad sanitizada pet-safe y
                        monitoreo en vivo.
                      </p>
                    </>
                  ) : (
                    <p className="mt-3 text-sm font-semibold opacity-80">
                      No pudimos calcular la tarifa automáticamente; la confirmamos por
                      WhatsApp.
                    </p>
                  )}
                </div>

                {error && (
                  <p className="mt-4 flex items-start gap-2 rounded-2xl bg-destructive/10 px-4 py-3 text-sm font-bold text-destructive">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                    {error}
                  </p>
                )}

                {bookingId ? (
                  <div className="mt-6 rounded-2xl bg-gold-soft px-5 py-4 text-center">
                    <p className="font-display text-lg font-bold text-primary">
                      ¡Reserva confirmada! 🌙
                    </p>
                    <p className="mt-1 text-sm font-semibold text-foreground">
                      Apartamos tu franja de {formatSlotLabel(data.hora)} el {data.fecha}. Si
                      no se abrió WhatsApp,{" "}
                      <a
                        href={buildWhatsAppUrlWithQuote(data, quote)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline"
                      >
                        envía tu solicitud aquí
                      </a>
                      .
                    </p>
                  </div>
                ) : (
                  <>
                  <label className="mt-6 flex cursor-pointer items-start gap-3 rounded-2xl bg-secondary/60 px-4 py-3.5 ring-1 ring-border">
                    <input
                      type="checkbox"
                      checked={acepta}
                      onChange={(e) => setAcepta(e.target.checked)}
                      className="mt-0.5 h-5 w-5 shrink-0 rounded-md accent-[hsl(var(--accent))]"
                    />
                    <span className="text-sm font-semibold leading-relaxed text-foreground">
                      Acepto los Términos de Servicio y declaro que mi mascota cuenta con
                      vacunas al día.{" "}
                      <SafetyPolicyModal />
                    </span>
                  </label>
                  <button
                    type="button"
                    onClick={() => void confirmBooking()}
                    disabled={
                      booking ||
                      !acepta ||
                      data.clienteNombre.trim().length < 2 ||
                      data.clienteTelefono.replace(/\D/g, "").length < 10
                    }
                    className="mt-6 flex w-full items-center justify-center gap-2.5 rounded-full bg-whatsapp px-8 py-4 text-base font-extrabold text-whatsapp-foreground shadow-soft transition-all hover:-translate-y-0.5 hover:brightness-105 disabled:opacity-60"
                  >
                    {booking ? (
                      <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
                    ) : (
                      <MessageCircle className="h-5 w-5" aria-hidden />
                    )}
                    Confirmar y Solicitar Viaje
                  </button>
                  {(!acepta ||
                    data.clienteNombre.trim().length < 2 ||
                    data.clienteTelefono.replace(/\D/g, "").length < 10) && (
                    <p className="mt-2 flex items-center justify-center gap-1.5 text-center text-xs font-bold text-muted-foreground">
                      <ShieldCheck className="h-3.5 w-3.5" aria-hidden />
                      Completa tu nombre, WhatsApp y acepta los términos para solicitar tu
                      viaje.
                    </p>
                  )}

                  </>
                )}
                <p className="mt-3 text-center text-xs font-semibold text-muted-foreground">
                  Apartamos tu horario al confirmar y te respondemos en minutos.
                </p>

                <div className="mt-6 border-t border-border pt-5 text-center">
                  <Link
                    to="/seguimiento"
                    className="inline-flex items-center gap-2 text-sm font-extrabold text-primary transition-colors hover:text-gold-deep"
                  >
                    <Car className="h-4 w-4" aria-hidden />
                    ¿Ya va en camino? Así se verá tu seguimiento en vivo
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Navigation */}
          {step < 3 && (
            <div className="mt-8 flex items-center justify-between gap-4">
              {step > 0 ? (
                <button
                  type="button"
                  onClick={() => setStep((s) => s - 1)}
                  className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-extrabold text-muted-foreground transition-colors hover:text-primary"
                >
                  <ArrowLeft className="h-4 w-4" aria-hidden />
                  Atrás
                </button>
              ) : (
                <span />
              )}
              <button
                type="button"
                disabled={!canContinue}
                onClick={() => setStep((s) => Math.min(s + 1, 3))}
                className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-extrabold text-primary-foreground shadow-soft transition-all hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0"
              >
                Continuar
                <ArrowRight className="h-4 w-4" aria-hidden />
              </button>
            </div>
          )}

          {step === 3 && (
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={() => setStep(2)}
                className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-extrabold text-muted-foreground transition-colors hover:text-primary"
              >
                <ArrowLeft className="h-4 w-4" aria-hidden />
                Editar fecha y hora
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
